import React from 'react';
import PropTypes from 'prop-types';
import Modal from '../Modal';
import './TreeDialogs.css';

const filesText = (count) => `${count} file${count === 1 ? '' : 's'}`;

/** What deleting `item` does, in one sentence (a folder: how many files go with it; null while counted, -1 unknown). */
function whatGoes(item, fileCount) {
  if (item.type !== 'tree') { return <>The file <strong>{item.path}</strong> will be deleted.</>; }
  if (fileCount < 0) { return <>The folder <strong>{item.path}</strong> and everything in it will be deleted.</>; }
  if (fileCount === null || fileCount === undefined) { return <>The folder <strong>{item.path}</strong> and everything in it will be deleted (counting the files…).</>; }
  if (fileCount === 0) { return <>The empty folder <strong>{item.path}</strong> will be deleted.</>; }
  return <>The folder <strong>{item.path}</strong> and the {filesText(fileCount)} in it will be deleted.</>;
}

/**
 * The Code tree's "Delete…?" (the app's own Modal, not the browser's confirm): it names what goes, and for a folder
 * how many files it holds; as wide as New file and Move to… (480px); Cancel, a light secondary button, has the focus and Escape closes it, so Enter never deletes by accident; Delete is red.
 * `restorable`: the delete is a commit on vfs-service, which History › Changes can bring back; otherwise (a git-backed
 * tree) the files are marked and go with the next push.
 */
const TreeDeleteConfirm = ({item, fileCount = null, restorable, busy = false, onConfirm, onCancel}) => (
  <Modal
    cancelText="Cancel"
    className="tree-dialog-modal"
    confirmText="Delete"
    focusCancel
    isDangerous
    isLoading={busy}
    isOpen
    message={(
      <span data-testid="tree-delete-message">
        {whatGoes(item, fileCount)}
        {' '}
        {restorable
          ? 'You can bring it back from History › Changes.'
          : 'It is removed from Git when you push your changes.'}
      </span>
    )}
    onCancel={busy ? () => {} : onCancel}
    onClose={busy ? null : onCancel}
    onConfirm={onConfirm}
    title={item.type === 'tree' ? `Delete folder “${item.name}”?` : `Delete “${item.name}”?`}
    type="confirm"
  />
);

TreeDeleteConfirm.propTypes = {
  busy: PropTypes.bool,
  fileCount: PropTypes.number,
  item: PropTypes.shape({name: PropTypes.string, path: PropTypes.string.isRequired, type: PropTypes.string}).isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  restorable: PropTypes.bool.isRequired,
};

export default TreeDeleteConfirm;
